import React from 'react';
import StaticPage from './StaticPage';

export default function Terms() {
  return (
    <StaticPage
      title="Terms of Service"
      subtitle="Last updated: February 1, 2026. Please read these terms carefully — they govern your use of ALFÀ and any care you receive through our platform."
      seoDescription="ALFÀ Terms of Service — the rules for using our telehealth platform, including consultations, prescriptions, refunds, and dispute resolution."
    >
      <h2>1. Acceptance of these terms</h2>
      <p>
        By accessing <a className="underline" href="https://alfameds.com/">alfameds.com</a>,
        creating an account, completing an intake quiz, or receiving services from ALFÀ, you
        agree to these Terms of Service and our{' '}
        <a className="underline" href="/privacy">Privacy Policy</a>. If you don't agree, please
        don't use the service.
      </p>

      <h2>2. Who can use ALFÀ</h2>
      <ul>
        <li>You must be at least 18 years old.</li>
        <li>You must be a U.S. resident and physically located in a state where our providers are licensed.</li>
        <li>You must provide accurate, complete, and current information about your medical history.</li>
      </ul>

      <h2>3. Nature of the service</h2>
      <p>
        ALFÀ connects you with independently licensed U.S. healthcare providers who may prescribe
        medication when clinically appropriate. ALFÀ itself is not a medical practice and does
        not provide medical advice — the providers you consult do. Prescribing decisions are
        entirely at each provider's clinical discretion.
      </p>

      <h2>4. Not a substitute for emergency care</h2>
      <p>
        ALFÀ is not designed for emergencies. If you have a medical emergency, call{' '}
        <strong>911</strong> or go to the nearest emergency room. Never delay seeking care
        because of information you read on our site.
      </p>

      <h2>5. Accounts and honesty</h2>
      <p>
        You are responsible for the accuracy of the information you provide. Providing false
        medical history is dangerous and can void your program. You are also responsible for
        maintaining the confidentiality of your login credentials and for any activity that
        occurs under your account.
      </p>

      <h2>6. Payments, subscriptions, and refunds</h2>
      <ul>
        <li>Programs are billed monthly (or as otherwise disclosed at checkout) via your saved payment method.</li>
        <li>You may cancel at any time from your account; cancellation stops future charges but does not refund shipments already sent.</li>
        <li>Because medications are custom compounded and cannot be re-dispensed, we generally cannot accept returns of dispensed product.</li>
        <li>If there is a shipping error, damaged product, or other issue on our end, contact <a className="underline" href="mailto:help@alfameds.com">help@alfameds.com</a> within 14 days and we'll make it right.</li>
      </ul>

      <h2>7. Prescriptions and refills</h2>
      <p>
        Prescriptions are valid only while you remain an active patient of the provider who
        issued them. Refills require an updated intake and provider review. We may pause or
        discontinue your program at any time if clinically warranted.
      </p>

      <h2>8. Prohibited uses</h2>
      <p>You agree not to:</p>
      <ul>
        <li>Resell, share, or transfer medications prescribed to you.</li>
        <li>Impersonate anyone else or misrepresent your medical history.</li>
        <li>Reverse-engineer, scrape, or interfere with the operation of our site.</li>
        <li>Use ALFÀ for any unlawful purpose.</li>
      </ul>

      <h2>9. Intellectual property</h2>
      <p>
        All content on alfameds.com — including the ALFÀ name and logo, article text, images,
        and design — is owned by ALFÀ or licensed to us. You may not copy, republish, or use it
        commercially without our written permission.
      </p>

      <h2>10. Disclaimers</h2>
      <p>
        The service is provided "as is". We make no warranties that the site will be
        uninterrupted or error-free. Educational content on our Health Guide is for general
        information only and is not medical advice for your specific situation.
      </p>

      <h2>11. Limitation of liability</h2>
      <p>
        To the fullest extent permitted by law, ALFÀ's total liability arising from your use of
        the service is limited to the amount you paid us in the twelve months preceding the
        event that gave rise to the claim. We are not liable for indirect, consequential, or
        punitive damages.
      </p>

      <h2>12. Governing law and disputes</h2>
      <p>
        These terms are governed by the laws of the State of Florida, without regard to conflict
        of laws principles. Any dispute will be resolved by binding individual arbitration in
        Sarasota County, Florida, except for small-claims-court matters or requests for
        injunctive relief.
      </p>

      <h2>13. Changes to these terms</h2>
      <p>
        We may update these terms from time to time. Material changes will be posted here with a
        revised "Last updated" date. Continued use of ALFÀ after changes take effect means you
        accept the new terms.
      </p>

      <h2>14. Contact</h2>
      <p>
        Questions about these terms? Email{' '}
        <a className="underline" href="mailto:help@alfameds.com">help@alfameds.com</a> or
        call <a className="underline" href="tel:+19419489940">941-948-9940</a>.
      </p>
    </StaticPage>
  );
}
